// [S-060] Login screen — port of docs/Wireframes/Login.html.
//
// Wireframe fidelity: identical class hierarchy + content. Visual
// values rendered from @csai/design-system tokens (see styles/login.css).
//
// Auth paths (in order of preference):
//   1. SSO (Microsoft / Okta / Google) — primary for studios with an
//      IDP federation already configured on their Identity Platform
//      tenant.
//   2. Email + password — the default fallback. The FirebaseAuthAdapter
//      calls signInWithEmailAndPassword against the resolved tenant.
//      Users need a password set in Identity Platform Console
//      (tenant → Users → set password).
//   3. Magic link — passwordless alternative; sends a sign-in link via
//      Identity Platform and the user completes via /auth/callback.
//
// Master Rule (S-060): JWT lives in memory only. The form does not store
// the email or session anywhere persistent — refresh re-mounts in the
// "anonymous" state and the user signs in again.
import { type FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { takeOAuthReturn } from "../auth/oauthReturn";
import { Button, Checkbox } from "../components";
import { useApi } from "../data/api";

// [S-206] Opt-in email remembering. We persist ONLY the email (never the
// password or a token), per-origin + per-device, so a returning user doesn't
// retype it. The checkbox makes it an explicit choice — important on shared
// machines — and the S-060 "no credentials persisted" rule is preserved.
const REMEMBERED_EMAIL_KEY = "csai.login.rememberedEmail";

function readRememberedEmail(): string {
  try {
    return window.localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? "";
  } catch {
    return ""; // localStorage unavailable (private mode / disabled)
  }
}

function writeRememberedEmail(email: string, remember: boolean): void {
  try {
    if (remember && email.trim().length > 0) {
      window.localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim());
    } else {
      window.localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    }
  } catch {
    // best-effort — a failed persist must never block sign-in
  }
}

export function Login(): JSX.Element {
  const { signIn, state } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // [S-637] The route the user was on when a guard bounced them here (session
  // expired or a protected deep-link). Present → came from an interrupted
  // session, so we (a) show a "session expired" note and (b) return them there
  // after re-auth instead of dumping them on /home. Guard against looping back
  // to /login.
  const fromLoc = (location.state as { from?: { pathname?: string; search?: string } } | null)
    ?.from;
  const returnTo =
    typeof fromLoc?.pathname === "string" &&
    fromLoc.pathname.startsWith("/") &&
    fromLoc.pathname !== "/login"
      ? `${fromLoc.pathname}${fromLoc.search ?? ""}`
      : null;
  const api = useApi("admin");
  // Pre-fill from a previously-remembered email; check the box when one exists.
  const [email, setEmail] = useState(readRememberedEmail);
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(
    () => readRememberedEmail().length > 0
  );
  // [S-616] self-serve password reset — one-shot confirmation message.
  const [resetMsg, setResetMsg] = useState<string | null>(null);

  const submitting = state.status === "authenticating";
  const errorMessage =
    state.status === "error" ? state.message : null;

  // Once the AuthProvider transitions to `authenticated`, send the user on
  // (to a pending OAuth return URL if any, else /home). useEffect (not inline
  // `queueMicrotask`) — the latter re-fired every render and looped in tests.
  useEffect(() => {
    if (state.status === "authenticated") {
      // S-475 — if the user arrived mid-OAuth (claude.ai MCP connector), return
      // them to the stashed consent URL (with its params) instead of /home.
      const pending = takeOAuthReturn();
      const dest =
        pending !== null && pending.startsWith("/")
          ? pending
          : (returnTo ?? "/home"); // [S-637] back to the interrupted route
      navigate(dest, { replace: true });
    }
  }, [state.status, navigate, returnTo]);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    // Email + password is the default. The FirebaseAuthAdapter throws
    // if password is empty; the magic-link button below covers the
    // passwordless path.
    writeRememberedEmail(email, remember);
    void signIn({ email, password, provider: "password" });
  }

  function handleMagicLink(): void {
    if (email.trim().length === 0) return;
    writeRememberedEmail(email, remember);
    // AuthProvider catches MagicLinkSentError from the adapter and
    // surfaces its message via state.status === "error"; the user sees
    // "Magic link sent to X; check your inbox." in the error slot.
    void signIn({ email, provider: "magic" });
  }

  // [S-616] "Forgot password?" — request a reset link. The endpoint is
  // constant-response (anti-enumeration), so we show the same reassurance
  // regardless of whether the email resolves to an account, and swallow errors.
  async function handleForgotPassword(): Promise<void> {
    if (email.trim().length === 0) {
      setResetMsg("Enter your work email above first, then tap Forgot password.");
      return;
    }
    try {
      await api<{ ok: boolean; message: string }>("/auth/forgot-password", {
        method: "POST",
        body: { email: email.trim() },
      });
    } catch {
      /* constant-response endpoint — never reveal anything on error */
    }
    setResetMsg(
      "If an account exists for that email, a password reset link is on its way — check your inbox."
    );
  }

  async function handleSso(provider: "microsoft" | "okta" | "google"): Promise<void> {
    if (email.trim().length === 0) {
      // The mock adapter requires an email; in production the SSO button
      // pops the IDP picker, but here we just hint the user.
      const probe = window.prompt(
        `Enter your work email to continue with ${provider}:`
      );
      if (probe === null || probe.trim().length === 0) return;
      writeRememberedEmail(probe.trim(), remember);
      await signIn({ email: probe.trim(), provider });
      return;
    }
    writeRememberedEmail(email, remember);
    await signIn({ email, provider });
  }

  return (
    <div className="login" data-screen-label="Login">
      <div className="login__left">
        <div className="login__brand">
          <span className="login__brand-mark">CS</span>
          <span className="login__brand-text">
            ContractStudio<em>.ai</em>
          </span>
        </div>

        <div className="login__hero">
          <div className="login__eyebrow">Verifiable legal intelligence</div>
          <h1 className="login__title">
            The certified templates
            <br />
            meet <em>your house's</em>
            <br />
            deal history.
          </h1>
          <p className="login__lede">
            Two trusted sources, one place. Every answer carries the
            attorney who certified it, the CBA it's anchored to, and a
            confidence score you can quote.
          </p>

          <div className="login__cite">
            <div className="login__cite__row">
              <span className="k">Source</span>
              <span className="v">
                <b>Templates</b> · TMPL-001 v2.1.0
              </span>
            </div>
            <div className="login__cite__row">
              <span className="k">Anchored to</span>
              <span className="v">SAG-AFTRA 2023 Theatrical</span>
            </div>
            <div className="login__cite__row">
              <span className="k">Certified by</span>
              <span className="v">Sarah Chen, Esq. · Mar 14, 2026</span>
            </div>
            <div className="login__cite__row">
              <span className="k">Confidence</span>
              <span className="v">
                <span className="ok">●</span> 0.91 — high
              </span>
            </div>
          </div>
        </div>

        <div className="login__foot">
          <span>© 2026 ContractStudio Inc.</span>
          <span>
            <a href="#security">Security</a> · <a href="#soc2">SOC 2</a> ·{" "}
            <a href="#version">v1.4.0</a>
          </span>
        </div>
      </div>

      <div className="login__right">
        <div className="login__rtop">
          <span>
            New organization? <a href="#sales">Talk to sales →</a>
          </span>
        </div>

        <form className="login__form" onSubmit={handleSubmit} aria-label="Sign in">
          <div className="login__formhdr">
            <div className="eyebrow">Sign in</div>
            <h2>Welcome back, counsel.</h2>
            <p>Continue with your studio's identity provider.</p>
          </div>

          {/* [S-637] Arrived here from an interrupted session (expired token or a
              protected deep-link) — say so, and we'll return you after sign-in. */}
          {returnTo !== null && state.status !== "authenticated" && (
            <p className="login__notice" role="status" data-testid="login-expired-note">
              Your session expired. Sign back in to pick up where you left off.
            </p>
          )}

          <div className="login__sso">
            <Button
              variant="bare"
              className="login__ssobtn"
              onClick={() => void handleSso("microsoft")}
              disabled={submitting}
            >
              <span className="logo logo--ms" aria-hidden="true">▦</span>
              Continue with Microsoft
              <span className="tenant">meridianpictures.com</span>
            </Button>
            <Button
              variant="bare"
              className="login__ssobtn"
              onClick={() => void handleSso("okta")}
              disabled={submitting}
            >
              <span className="logo logo--okta" aria-hidden="true">○</span>
              Continue with Okta
              <span className="tenant">SSO</span>
            </Button>
            <Button
              variant="bare"
              className="login__ssobtn"
              onClick={() => void handleSso("google")}
              disabled={submitting}
            >
              <span className="logo" aria-hidden="true">G</span>
              Continue with Google Workspace
            </Button>
          </div>

          <div className="login__divider">or use email</div>

          <div className="login__field">
            <label htmlFor="login-email">Work email</label>
            <input
              id="login-email"
              type="email"
              required
              autoComplete="email"
              placeholder="jrivera@meridianpictures.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="login__field">
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              type="password"
              required
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="login__remember">
            <Checkbox
              id="login-remember"
              checked={remember}
              onCheckedChange={setRemember}
              disabled={submitting}
              label="Remember my email on this device"
              aria-label="Remember my email on this device"
              data-testid="login-remember"
            />
          </div>

          <Button
            type="submit"
            variant="bare"
            className="login__primary"
            disabled={submitting}
          >
            {submitting ? "Signing in…" : "Sign in →"}
          </Button>

          <Button
            type="button"
            variant="bare"
            className="login__secondary"
            onClick={handleMagicLink}
            disabled={submitting || email.trim().length === 0}
          >
            Email me a magic link instead
          </Button>

          <Button
            type="button"
            variant="bare"
            className="login__forgot"
            onClick={() => void handleForgotPassword()}
            disabled={submitting}
            data-testid="login-forgot-password"
          >
            Forgot password?
          </Button>

          {resetMsg !== null && (
            <p className="login__notice" role="status" data-testid="login-reset-notice">
              {resetMsg}
            </p>
          )}

          {errorMessage !== null && (
            <p className="login__error" role="alert">
              {errorMessage}
            </p>
          )}

          <p className="login__legal">
            By signing in you agree to ContractStudio's{" "}
            <a href="#terms">Terms</a> and confirm that contract content
            processed through your organization remains in your cloud.
          </p>

          <div className="login__rfoot">
            <span>v1.4.0 · MCP nominal</span>
            <span className="pill">
              <span className="dot" /> SOC 2 Type II
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
